import {
  Component,
  Input,
  OnInit,
  inject,
  OnChanges,
  SimpleChanges,
  input,
  output,
  ChangeDetectionStrategy,
} from "@angular/core";
import { Subscription } from "rxjs";
import { SelectItem } from "primeng/api";
import { HttpParams } from "@angular/common/http";
import { CompanyRepository } from "../../../company/company.repository";
import { Company } from "../../../company/company.model";
import { Organization } from "app/organization/organization.model";
import { role } from "app/user/role.const";
import { UserService } from "app/user/user.service";
import { NgxPermissionsModule } from "ngx-permissions";
import { Bind } from "primeng/bind";
import { Select } from "primeng/select";
import { SelectedChipsComponent } from "../../chip/selected-chips.component";
import { Checkbox } from "primeng/checkbox";
import { FormsModule } from "@angular/forms";

export interface CompanyChangeEvent {
  originalEvent: MouseEvent;
  value: Company[];
}

@Component({
  selector: "app-company-multiselect",
  templateUrl: "./company-multiselect.component.html",
  styleUrls: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    NgxPermissionsModule,
    Bind,
    Select,
    Checkbox,
    FormsModule,
    SelectedChipsComponent,
  ],
})
export class CompanyMultiselectComponent implements OnInit, OnChanges {
  private companyRepository = inject(CompanyRepository);
  private userService = inject(UserService);

  public readonly companyChange = output<Company[]>();
  @Input() public companies: Company[];
  public readonly companyIds = input<number[]>(undefined);
  public readonly organization = input<Organization>(undefined);
  public readonly hideWhenEmpty = input<boolean>(false);
  public readonly disabled = input(false);
  public readonly required = input(false);

  public role = role;
  public subscription = new Subscription();
  public companyList: SelectItem[] = [];
  public loaded = false;
  public hasCompanies = false;

  ngOnInit() {
    this.buildCompanyList();
  }

  ngOnChanges(changes: SimpleChanges) {
    const companyIds = this.companyIds();
    if (companyIds && companyIds.length) {
      this.updateSelectedCompanies();
    } else if (changes.companyIds && !changes.companyIds.isFirstChange()) {
      // Clearing the filter passes an empty list, which `updateSelectedCompanies`
      // ignores (it only maps ids onto companies) — drop the chips here.
      this.companies = [];
    }
    if (changes.organization && !changes.organization.isFirstChange()) {
      this.buildCompanyList();
    }
  }

  public onCompanyChange(event: Company[]) {
    this.companyChange.emit(event);
  }

  public readonly chipLabel = (company: Company): string => company.name;
  public readonly chipKey = (company: Company): number => company.getId();

  public removeCompany(company: Company) {
    this.companies = (this.companies ?? []).filter(
      (item) => item.getId() !== company.getId(),
    );
    this.onCompanyChange(this.companies);
  }

  public isSelected(company: Company): boolean {
    return (this.companies ?? []).some(
      (item) => item.getId() === company.getId(),
    );
  }

  private updateSelectedCompanies(): void {
    const companyIds = this.companyIds();
    if (companyIds && companyIds.length && this.companyList.length) {
      const companies = [];
      for (const id of companyIds) {
        const exists = this.companyList.find(
          (item) => item.value.getId() == id,
        );
        if (exists) {
          companies.push(exists.value);
        }
      }
      this.companies = companies;
    }
  }

  private buildCompanyList(): void {
    this.loaded = false;
    this.companyList = [];

    this.userService.getUser().then((user) => {
      const organization = this.organization();
      // An explicit organization scopes the picker to another org, so the current user's own companies must not gate it.
      this.hasCompanies = !!organization || !!user?.hasCompanies();

      if (!this.hasCompanies) {
        return;
      }

      if (
        user.hasRole(role.customerService) ||
        user.hasRole(role.pricingManager) ||
        user.hasRole(role.organizationAdmin)
      ) {
        if (organization) {
          this.companyRepository
            .listByOrganization(organization.getId())
            .subscribe((companyList) => {
              companyList.forEach((company) => {
                this.companyList.push({ label: company.name, value: company });
              });
              this.companyList = [...this.companyList];
              this.loaded = true;
              this.updateSelectedCompanies();
            });
        } else if (user.canListCompanies()) {
          const params = new HttpParams();
          this.companyRepository.list(params).subscribe((companyList) => {
            companyList.forEach((company) => {
              this.companyList.push({ label: company.name, value: company });
            });
            this.companyList = [...this.companyList];
            this.loaded = true;
            this.updateSelectedCompanies();
          });
        }
      }
    });
  }
}
